import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { InternalPage } from 'theSource/components';
import { useConfigurationQuery } from 'theSource/queries';
import { ErrorResponseDto } from 'theSource/types';
import { queryKeyGenerator, useApiMutation } from 'theSource/utils';
import { useBenefitCategoryQuery } from 'enrollment/queries';
import { BackLink } from 'enrollment/components';
import { BenefitDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { ESignatureBox } from './components';
import { PsButtonTuner } from 'stencilSetup';
import { ElectRequestDto } from '../Plans/PlanCard/types';
import { ElectResponseDto } from '../Plans/Volume/types/dtos';
import { ROUTES } from 'enrollment/app/routes';
import { selectIsAdmin } from 'theSource/features';

export const ESignaturesReview = () => {
  const { t } = useTranslation('enrollment.esign');
  const navigate = useNavigate();
  const isAdmin = useSelector(selectIsAdmin);

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const enrollmentContext = useSelector(selectEnrollmentContext);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContext === 'life_event' ? orgPlanYearContextId : 0;

  const {
    data: benefitData,
    isFetching: isFetchingBenefits,
    isLoading: isLoadingBenefits,
  } = useBenefitCategoryQuery({
    key: queryKeyGenerator(
      `benefits-details-${enrollmentContext}`,
      Number(orgPlanYearContextId)
    ),
    enrollmentContextType: enrollmentContext,
    enabled: true,
    lumosApiHost: String(lumosApiHost),
    includeEligibleDependentsData: true,
    orgPlanYearId: orgPlanYearContextId,
  });

  const esignBenefits = benefitData?.data?.benefits?.filter(
    (benefit: BenefitDto) =>
      benefit.coverage &&
      benefit.coverage.esign_data !== null &&
      benefit.coverage.esign_data?.requires_esign
  );

  const { isLoading: isEditing, mutate: esignElection } = useApiMutation<
    ElectResponseDto,
    ErrorResponseDto,
    ElectRequestDto
  >({
    queryKey: 'esign',
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/benefits/elect`,
    queryKeysToInvalidate: queryKeyGenerator(
      `benefits-details-${enrollmentContext}`,
      Number(orgPlanYearContextId)
    ),
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const disableInputs = (planId: number) => {
    const esignCoverage = esignBenefits?.filter(
      (benefit) => benefit.coverage?.req_org_plan_id === planId
    )[0].coverage;

    if (!esignCoverage) {
      return true;
    }

    return (
      isFetchingBenefits ||
      isLoadingBenefits ||
      isEditing ||
      esignCoverage?.esign_data?.esigned ||
      (isAdmin && !esignCoverage.esign_data?.allow_admin_esign)
    );
  };

  const submitEsignature = (
    esignBenefit: BenefitDto,
    password?: string,
    checked?: boolean
  ) => {
    const benefit = esignBenefit.benefit;
    const coverage = esignBenefit.coverage;

    const params = {
      org_benefit_id: benefit.org_benefit_id,
      enrollment_context_type: enrollmentContext,
      election: {
        ...coverage,
      },
      esign_password: password,
      esign_checked: checked,
    } as ElectRequestDto;

    esignElection(params);
  };

  const disableContinue = esignBenefits?.some(
    (benefit) => !benefit.coverage?.esign_data?.esigned
  );

  const handleContinue = () => {
    navigate(`/enrollment/${ROUTES.enrollment.benefitDetails.path}`);
  };

  return (
    <InternalPage>
      <div
        data-testid="esignatures-review"
        className="pt-5 pb-4 pt-2_5 px-4 pt-md-5 px-md-0 mt-md-5 mt-lg-0"
      >
        <BackLink className="my-3 mt-md-5 mb-md-2 mt-lg-0 pt-md-3_5 pt-lg-0" />
        <h1>{t('title')}</h1>
        <p>{t('subTitle')}</p>
        <div id="provideEsignatures" className="card pb-0">
          {esignBenefits &&
            esignBenefits.map((esignBenefit: BenefitDto, index: number) => (
              <ESignatureBox
                key={index}
                esignBenefit={esignBenefit}
                submitEsign={submitEsignature}
                disabled={disableInputs}
              />
            ))}
        </div>
        <PsButtonTuner
          data-testid="looks-good-continue"
          buttonId="looks-good-continue"
          label={t('looksGood')}
          level="primary"
          disabled={disableContinue}
          onClick={handleContinue}
        />
      </div>
    </InternalPage>
  );
};
